import React, { useState, useRef, useEffect } from 'react';
import styles from './QuizDropdown.module.scss';
import classNames from 'classnames';
import ArrowIcon from '../../../assets/icons/arrow.svg';
import CheckIcon from '../../../assets/icons/check.svg';
import { insuranceCompanies } from '../../../config/insuranceCompanies';

interface QuizDropdownProps {
  value?: string;
  onSubmit: (value: string) => void;
  placeholder?: string;
  options?: string[];
}

const QuizDropdown: React.FC<QuizDropdownProps> = ({
  value: initialValue = '',
  onSubmit,
  placeholder = 'Bitte auswählen...',
  options = insuranceCompanies,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(initialValue);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement>(null);

  // Sync selectedValue with prop value
  useEffect(() => {
    setSelectedValue(initialValue);
  }, [initialValue]);

  // Filter options based on search
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to selected option when dropdown opens
  useEffect(() => {
    if (isOpen && selectedOptionRef.current && optionsListRef.current) {
      selectedOptionRef.current.scrollIntoView({
        block: 'center',
        behavior: 'auto',
      });
    }
  }, [isOpen]);

  const handleSelect = (option: string) => {
    setSelectedValue(option);
    setIsOpen(false);
    setSearchValue('');
    onSubmit(option);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  return (
    <div className={styles.quizDropdown} ref={dropdownRef}>
      <div
        className={classNames(styles.selectTrigger, { [styles.open]: isOpen })}
        onClick={() => setIsOpen(!isOpen)}
      >
        <input
          type="text"
          value={isOpen ? searchValue : selectedValue}
          onChange={handleInputChange}
          placeholder={selectedValue || placeholder}
          className={styles.searchInput}
        />
        <img src={ArrowIcon} alt="" className={styles.arrowIcon} />
      </div>

      {isOpen && (
        <div className={styles.optionsList} ref={optionsListRef}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                ref={option === selectedValue ? selectedOptionRef : null}
                className={classNames(styles.option, {
                  [styles.selected]: option === selectedValue,
                })}
                onClick={() => handleSelect(option)}
              >
                <span>{option}</span>
                {option === selectedValue && (
                  <img src={CheckIcon} alt="Selected" className={styles.checkIcon} />
                )}
              </div>
            ))
          ) : (
            <div className={styles.noOptions}>Keine Ergebnisse gefunden</div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizDropdown;
